import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { IonBadge, IonContent, IonMenuToggle, MenuController } from '@ionic/angular/standalone';

import { environment } from '@env';

import { AuthStore } from '@core/auth';
import {
  I18nPipe,
  I18nService,
  LANGUAGE_OPTIONS,
  LANGUAGE_OPTIONS_BY_LOCALE,
  type LanguageOption,
} from '@core/i18n';
import { ROUTES } from '@core/nav';

import { IconComponent } from '@shared/icon';
import { SelectComponent, SelectOptionDirective, SelectValueDirective } from '@shared/select';

import { resolveShellNavBadges } from '../shell-nav-notifications.util';
import { ShellStore } from '../shell.store';
import {
  MENU_COLLAPSED_CONTROL_CLASS,
  MENU_CONTENT_WIDTH_CLASS,
  MENU_ICON_TRACK_CLASS,
  MENU_LOGO_FULL,
  MENU_LOGO_MARK,
  MENU_LOGO_MARK_CLASS,
  MENU_NAV_ITEMS,
  type MenuLinkItem,
} from './menu.types';

@Component({
  selector: 'ml-shell-menu',
  imports: [
    IconComponent,
    I18nPipe,
    IonBadge,
    IonContent,
    IonMenuToggle,
    FormsModule,
    RouterLink,
    RouterLinkActive,
    SelectComponent,
    SelectOptionDirective,
    SelectValueDirective,
  ],
  host: {
    class: 'ml-shell-menu',
    '[class.icon-mode]': 'collapsed()',
  },
  templateUrl: './menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellMenuComponent {
  collapsed = input<boolean>(false);

  private readonly authStore = inject(AuthStore);
  private readonly i18n = inject(I18nService);
  private readonly shellStore = inject(ShellStore);
  private readonly menuController = inject(MenuController);

  protected readonly languageOptions = LANGUAGE_OPTIONS;
  protected readonly contentWidthClass = MENU_CONTENT_WIDTH_CLASS;
  protected readonly collapsedControlClass = MENU_COLLAPSED_CONTROL_CLASS;
  protected readonly iconTrackClass = MENU_ICON_TRACK_CLASS;
  protected readonly logoMarkClass = MENU_LOGO_MARK_CLASS;
  protected readonly logoFull = MENU_LOGO_FULL;
  protected readonly logoMark = MENU_LOGO_MARK;
  protected readonly moreRoute = ROUTES.more;
  protected readonly appVersion = environment.appVersion;
  protected readonly isProduction = environment.production;

  protected readonly secondaryLinks: readonly MenuLinkItem[] = [
    { id: 'tutorials', labelKey: 'shell.menu.tutorials', url: environment.tutorialsURL },
    { id: 'contact', labelKey: 'shell.menu.contact', url: environment.contactUsUrl },
    { id: 'survey', labelKey: 'shell.menu.survey', url: environment.surveyUrl },
    { id: 'tos', labelKey: 'shell.menu.tos', url: environment.tosUrl },
    { id: 'privacy', labelKey: 'shell.menu.privacy', url: environment.privacyUrl },
  ];

  protected readonly navItems = MENU_NAV_ITEMS;
  protected readonly selectedLanguage = computed<LanguageOption>(
    () => LANGUAGE_OPTIONS_BY_LOCALE[this.i18n.currentLang()],
  );
  protected readonly navBadges = computed(() =>
    resolveShellNavBadges(this.shellStore.navNotificationCounts()),
  );

  protected onLanguageChange(option: LanguageOption | null): void {
    if (option) void this.i18n.setLanguage(option.value);
  }

  protected async logout(): Promise<void> {
    this.authStore.endSession('manual');
    await this.menuController.close('shell-menu');
  }
}
